<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Premium Analytics Header -->
    <v-sheet
      color="white"
      class="pa-8 mb-8 border-bottom shadow-sm"
    >
      <v-row align="center">
        <v-col cols="12" md="6">
          <div class="d-flex align-center text-left">
            <v-avatar size="56" color="primary lighten-5" class="mr-4 rounded-xl shadow-xs overflow-hidden">
              <v-icon color="primary" large>fas fa-file-invoice-dollar</v-icon>
            </v-avatar>
            <div class="text-left">
              <div class="d-flex align-center mb-1">
                <h1 class="text-h4 font-weight-black primary--text tracking-tighter">Capital por Facturar</h1>
                <v-chip x-small color="success" class="ml-4 font-weight-black"></v-chip>
              </div>
              <p class="body-2 grey--text font-weight-medium mb-0 text-left">
                Control de Órdenes de Trabajo terminadas en planta pendientes de emisión DTE | Actualizado al {{ reportDate || 'Cargando...' }}
              </p>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6" class="d-flex justify-md-end align-center">
          <!-- Contenedor Filtro Fecha Redondeado (2 Niveles Limpios) -->
          <div class="d-flex align-center bg-lightgrey px-4 py-2 rounded-xl border shadow-xs mr-3">
            <v-icon small color="grey darken-1" class="mr-3">fas fa-calendar-day</v-icon>
            <div class="d-flex flex-column text-left">
              <span class="grey--text text--darken-1 font-weight-bold lh-1 mb-0" style="font-size: 0.65rem; line-height: 1; letter-spacing: 0.5px; text-transform: uppercase;">TERMINADAS DESDE</span>
              <input
                type="date"
                v-model="startDate"
                @change="loadDashboard"
                class="caption font-weight-black primary--text pointer-cursor mt-1"
                style="border: none; outline: none; background: transparent; font-family: inherit; font-size: 0.85rem;"
              />
            </div>
          </div>

          <v-btn
            color="primary"
            class="rounded-xl px-5 font-weight-black text-none shadow-sm mr-2"
            @click="loadDashboard"
            :loading="loading"
          >
            <v-icon left size="14">fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>

          <v-btn
            color="success"
            class="rounded-xl px-5 font-weight-black text-none shadow-sm"
            @click="downloadExcel"
            :loading="downloading"
          >
            <v-icon left size="14">fas fa-file-excel</v-icon>
            Exportar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-8">
      <!-- Loading Skeleton -->
      <v-row v-if="loading && !sellers.length" dense class="mb-8">
        <v-col v-for="i in 3" :key="i" cols="12" md="4">
          <v-skeleton-loader type="card" height="130" class="rounded-xl"></v-skeleton-loader>
        </v-col>
      </v-row>

      <!-- Bespoke Enterprise Metric Cards (Estilo Idéntico a Home.vue, Altura Uniforme h-100) -->
      <v-row v-else dense class="metrics-grid mb-8">
        <!-- Card 1: Capital Retenido Total -->
        <v-col cols="12" md="4">
          <v-card class="bespoke-metric-card h-100" flat>
            <div class="metric-accent blue-accent"></div>
            <v-card-text class="pa-6 d-flex flex-column justify-space-between h-100">
              <div>
                <div class="metric-label">Capital Retenido Total</div>
                <div class="metric-value-container">
                  <span class="value">{{ totalNetFmt || '$0' }}</span>
                </div>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="blue" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">{{ totalCount }} OTs finalizadas sin facturar</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Card 2: Promedio Días sin Facturar -->
        <v-col cols="12" md="4">
          <v-card class="bespoke-metric-card h-100" flat>
            <div class="metric-accent orange-accent"></div>
            <v-card-text class="pa-6 d-flex flex-column justify-space-between h-100">
              <div>
                <div class="metric-label">Promedio Días sin Facturar</div>
                <div class="metric-value-container">
                  <span class="value">{{ avgDaysPending }}</span>
                  <span class="currency-symbol ml-1">días</span>
                </div>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="warning" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Desde cierre real en taller</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Card 3: Índice IEF Global (Altura Uniforme h-100) -->
        <v-col cols="12" md="4">
          <v-card class="bespoke-metric-card h-100" flat>
            <div class="metric-accent success-accent"></div>
            <v-card-text class="pa-6 d-flex flex-column justify-space-between h-100">
              <div>
                <div class="metric-label">Índice IEF Global</div>
                <div class="metric-value-container">
                  <span class="value">{{ overallIef }}%</span>
                </div>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="success" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Facturado dentro de &le; 3 días</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tactical Insight Banner (100% Left-Aligned) -->
      <v-row v-if="tacticalInsight" class="mb-8">
        <v-col cols="12">
          <v-card color="#0F172A" dark flat class="rounded-xl pa-6 border-top-accent elevation-2 text-left">
            <div class="d-flex align-center mb-3 justify-start">
              <v-chip color="success" x-small class="font-weight-black text-uppercase tracking-widest px-3 mr-3">
                INSIGHT TÁCTICO DEL DÍA
              </v-chip>
              <div class="text-subtitle-1 font-weight-black yellow--text text--accent-2">{{ tacticalInsight.title }}</div>
            </div>
            <div class="text-body-1 grey--text text--lighten-2 mb-4 text-left lh-16" v-html="tacticalInsight.body"></div>
            <div v-if="tacticalInsight.highlight" class="text-left">
              <v-chip color="yellow darken-1" text-color="black" small class="font-weight-black text-uppercase px-3">
                {{ tacticalInsight.highlight }}
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Matriz de Tiempos de Facturación (Semáforo) -->
      <v-row v-if="agingBuckets" class="mb-8">
        <v-col cols="12">
          <div class="metric-label mb-3">MATRIZ DE TIEMPOS DE FACTURACIÓN (ANTIGÜEDAD DE OTS)</div>
        </v-col>
        
        <v-col cols="12" md="4">
          <v-card flat class="pa-5 rounded-xl bg-emerald-subtle border-emerald">
            <div class="caption font-weight-black text-uppercase success--text text--darken-2 d-flex align-center">
              <span class="dot dot-emerald mr-2"></span> NORMAL (0-3 DÍAS)
            </div>
            <div class="text-h4 font-weight-black success--text text--darken-3 mt-2">{{ agingBuckets.normal.net_fmt }}</div>
            <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
              {{ agingBuckets.normal.count }} OTs ({{ agingBuckets.normal.pct }}%)
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card flat class="pa-5 rounded-xl bg-amber-subtle border-amber">
            <div class="caption font-weight-black text-uppercase warning--text text--darken-3 d-flex align-center">
              <span class="dot dot-amber mr-2"></span> ATENCIÓN (4-10 DÍAS)
            </div>
            <div class="text-h4 font-weight-black warning--text text--darken-4 mt-2">{{ agingBuckets.attention.net_fmt }}</div>
            <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
              {{ agingBuckets.attention.count }} OTs ({{ agingBuckets.attention.pct }}%)
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card flat class="pa-5 rounded-xl bg-rose-subtle border-rose">
            <div class="caption font-weight-black text-uppercase error--text text--darken-2 d-flex align-center">
              <span class="dot dot-rose mr-2"></span> CRÍTICO (>10 DÍAS)
            </div>
            <div class="text-h4 font-weight-black error--text text--darken-3 mt-2">{{ agingBuckets.critical.net_fmt }}</div>
            <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
              {{ agingBuckets.critical.count }} OTs ({{ agingBuckets.critical.pct }}%)
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- DataTables Tabs Section -->
      <v-row class="mb-8">
        <v-col cols="12">
          <v-card flat class="bespoke-metric-card rounded-xl">
            <v-tabs v-model="activeTab" background-color="grey lighten-4" active-class="white primary--text font-weight-black elevation-1" grow>
              <v-tab class="font-weight-bold text-uppercase tracking-widest">
                <v-icon left small>fas fa-user-tie</v-icon> Resumen por Vendedor ({{ sellers.length }})
              </v-tab>
              <v-tab class="font-weight-bold text-uppercase tracking-widest">
                <v-icon left small>fas fa-building</v-icon> Resumen por Cliente ({{ customers.length }})
              </v-tab>
              <v-tab class="font-weight-bold text-uppercase tracking-widest">
                <v-icon left small>fas fa-list-check</v-icon> Detalle de OTs Pendientes ({{ workOrders.length }})
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab" class="pa-6">
              <!-- Tab 1: Resumen por Vendedor -->
              <v-tab-item>
                <v-row class="mb-4">
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field
                      v-model="searchSeller"
                      prepend-inner-icon="search"
                      label="Buscar vendedor..."
                      dense
                      outlined
                      hide-details
                      clearable
                      class="rounded-lg"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headersSeller"
                  :items="sellers"
                  :search="searchSeller"
                  :loading="loading"
                  loading-text="Cargando resumen de ejecutivos..."
                  no-data-text="No hay vendedores con OTs pendientes desde la fecha seleccionada"
                  class="elevation-0"
                >
                  <template v-slot:item.seller_name="{ item }">
                    <span class="font-weight-black primary--text">{{ item.seller_name }}</span>
                  </template>
                  <template v-slot:item.count="{ item }">
                    <v-chip x-small color="grey lighten-3" class="font-weight-black px-3">
                      {{ item.count }} OT{{ item.count !== 1 ? 's' : '' }}
                    </v-chip>
                  </template>
                  <template v-slot:item.avg_days="{ item }">
                    <v-chip
                      x-small
                      :color="getDaysColor(item.avg_days)"
                      dark
                      class="font-weight-black px-3"
                    >
                      {{ item.avg_days }}d
                    </v-chip>
                  </template>
                  <template v-slot:item.ief_score="{ item }">
                    <span :class="getIefClass(item.ief_score)" class="font-weight-black">
                      {{ item.ief_score }}%
                    </span>
                  </template>
                  <template v-slot:item.total_net_fmt="{ item }">
                    <span class="font-weight-black success--text text--darken-2">{{ item.total_net_fmt }}</span>
                  </template>
                </v-data-table>
              </v-tab-item>

              <!-- Tab 2: Resumen por Cliente -->
              <v-tab-item>
                <v-row class="mb-4">
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field
                      v-model="searchCustomer"
                      prepend-inner-icon="search"
                      label="Buscar cliente..."
                      dense
                      outlined
                      hide-details
                      clearable
                      class="rounded-lg"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headersCustomer"
                  :items="customers"
                  :search="searchCustomer"
                  :loading="loading"
                  loading-text="Cargando resumen por cliente..."
                  no-data-text="No hay clientes con OTs pendientes desde la fecha seleccionada"
                  class="elevation-0"
                >
                  <template v-slot:item.customer_name="{ item }">
                    <span class="font-weight-black primary--text">{{ item.customer_name }}</span>
                  </template>
                  <template v-slot:item.count="{ item }">
                    <v-chip x-small color="grey lighten-3" class="font-weight-black px-3">
                      {{ item.count }} OT{{ item.count !== 1 ? 's' : '' }}
                    </v-chip>
                  </template>
                  <template v-slot:item.avg_days="{ item }">
                    <v-chip
                      x-small
                      :color="getDaysColor(item.avg_days)"
                      dark
                      class="font-weight-black px-3"
                    >
                      {{ item.avg_days }}d
                    </v-chip>
                  </template>
                  <template v-slot:item.ief_score="{ item }">
                    <span :class="getIefClass(item.ief_score)" class="font-weight-black">
                      {{ item.ief_score }}%
                    </span>
                  </template>
                  <template v-slot:item.total_net_fmt="{ item }">
                    <span class="font-weight-black success--text text--darken-2">{{ item.total_net_fmt }}</span>
                  </template>
                </v-data-table>
              </v-tab-item>

              <!-- Tab 3: Detalle OTs Pendientes -->
              <v-tab-item>
                <v-row class="mb-4">
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field
                      v-model="searchWorkOrder"
                      prepend-inner-icon="search"
                      label="Buscar OT, Cotización, Cliente o Vendedor..."
                      dense
                      outlined
                      hide-details
                      clearable
                      class="rounded-lg"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headersWorkOrder"
                  :items="workOrders"
                  :search="searchWorkOrder"
                  :loading="loading"
                  loading-text="Cargando detalle de OTs..."
                  no-data-text="No hay Órdenes de Trabajo pendientes desde la fecha seleccionada"
                  class="elevation-0"
                >
                  <template v-slot:item.quote_id="{ item }">
                    <span class="grey--text font-weight-bold">#{{ item.quote_id || '-' }}</span>
                  </template>
                  <template v-slot:item.work_order_id="{ item }">
                    <v-chip small color="primary lighten-5" class="primary--text font-weight-black">
                      OT-{{ item.work_order_id }}
                    </v-chip>
                  </template>
                  <template v-slot:item.customer="{ item }">
                    <span class="font-weight-bold">{{ item.customer || 'Sin Cliente' }}</span>
                  </template>
                  <template v-slot:item.seller_name="{ item }">
                    <span class="caption font-weight-bold grey--text text--darken-2">{{ item.seller_name || 'Sin Vendedor' }}</span>
                  </template>
                  <template v-slot:item.days_pending="{ item }">
                    <v-chip
                      x-small
                      :color="getDaysColor(item.days_pending)"
                      dark
                      class="font-weight-black px-3"
                    >
                      {{ item.days_pending }}d
                    </v-chip>
                  </template>
                  <template v-slot:item.net_amount_fmt="{ item }">
                    <span class="font-weight-black success--text text--darken-2">{{ item.net_amount_fmt }}</span>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'InvoicingDashboard',
  data() {
    return {
      loading: false,
      downloading: false,
      activeTab: 0,
      startDate: moment().subtract(90, 'days').format('YYYY-MM-DD'),
      reportDate: '',
      timezoneName: '',
      totalCount: 0,
      totalAmount: 0,
      totalNetFmt: '',
      totalGrossFmt: '',
      avgDaysPending: 0,
      overallIef: 0,
      agingBuckets: null,
      tacticalInsight: null,
      sellers: [],
      customers: [],
      workOrders: [],
      searchSeller: '',
      searchCustomer: '',
      searchWorkOrder: '',
      headersSeller: [
        { text: 'Ejecutivo de Ventas', value: 'seller_name', align: 'left', sortable: true },
        { text: 'OTs Pendientes', value: 'count', align: 'center', sortable: true },
        { text: 'Promedio Días sin Facturar', value: 'avg_days', align: 'center', sortable: true },
        { text: 'Índice IEF (%)', value: 'ief_score', align: 'center', sortable: true },
        { text: 'Monto Neto Total', value: 'total_net_fmt', align: 'right', sortable: true },
      ],
      headersCustomer: [
        { text: 'Cliente', value: 'customer_name', align: 'left', sortable: true },
        { text: 'OTs Pendientes', value: 'count', align: 'center', sortable: true },
        { text: 'Promedio Días sin Facturar', value: 'avg_days', align: 'center', sortable: true },
        { text: 'Índice IEF (%)', value: 'ief_score', align: 'center', sortable: true },
        { text: 'Monto Neto Total', value: 'total_net_fmt', align: 'right', sortable: true },
      ],
      headersWorkOrder: [
        { text: 'Cotización', value: 'quote_id', align: 'left', sortable: true },
        { text: 'N° OT', value: 'work_order_id', align: 'left', sortable: true },
        { text: 'Cliente', value: 'customer', align: 'left', sortable: true },
        { text: 'Vendedor', value: 'seller_name', align: 'left', sortable: true },
        { text: 'Término en Planta', value: 'completed_at', align: 'center', sortable: true },
        { text: 'Días sin Facturar', value: 'days_pending', align: 'center', sortable: true },
        { text: 'Monto Neto', value: 'net_amount_fmt', align: 'right', sortable: true },
      ],
    }
  },
  computed: {
    parameters() {
      return this.$store.getters.getCompany || {}
    },
  },
  mounted() {
    this.loadDashboard()
  },
  methods: {
    async loadDashboard() {
      this.loading = true
      try {
        let url = 'sales/invoicing-dashboard'
        if (this.startDate) {
          url += `?start_date=${this.startDate}`
        }
        const response = await axios.get(url)
        const data = response.data
        this.reportDate = data.report_date
        this.timezoneName = data.timezone_name
        this.totalCount = data.total_count
        this.totalAmount = data.total_amount
        this.totalNetFmt = data.total_net_fmt
        this.totalGrossFmt = data.total_gross_fmt
        this.avgDaysPending = data.avg_days_pending
        this.overallIef = data.overall_ief
        this.agingBuckets = data.aging_buckets
        this.tacticalInsight = data.tactical_insight
        this.sellers = data.sellers || []
        this.customers = data.customers || []
        this.workOrders = data.work_orders || []
      } catch (error) {
        this.$store.dispatch('showNotification', {
          text: 'Error al cargar los datos de Capital por Facturar',
          color: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    async downloadExcel() {
      this.downloading = true
      try {
        let url = 'sales/invoicing-dashboard/export-excel'
        if (this.startDate) {
          url += `?start_date=${this.startDate}`
        }
        const response = await axios.get(url, {
          responseType: 'blob',
        })
        const urlBlob = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = urlBlob
        link.setAttribute('download', `capital_por_facturar_${this.startDate || 'desde_siempre'}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        this.$store.dispatch('showNotification', {
          text: 'Error al descargar la planilla Excel',
          color: 'error',
        })
      } finally {
        this.downloading = false
      }
    },
    getDaysColor(days) {
      if (days <= 3) return 'green'
      if (days <= 8) return 'warning'
      return 'error'
    },
    getIefClass(score) {
      if (score >= 80) return 'success--text'
      if (score >= 60) return 'warning--text text--darken-2'
      return 'error--text'
    },
  },
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.bg-lightgrey {
  background-color: #f1f5f9 !important;
}

.pointer-cursor {
  cursor: pointer;
}

.tracking-tighter {
  letter-spacing: -1.5px !important;
}

.lh-1 {
  line-height: 1 !important;
}

.bespoke-metric-card {
  position: relative;
  background: white !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  border-radius: 16px !important;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  overflow: hidden;
}

.bespoke-metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06) !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}

.metric-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.blue-accent { background: #35BDC8; }
.orange-accent { background: #ff9800; }
.success-accent { background: #4caf50; }

.metric-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.metric-value-container {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.currency-symbol {
  font-size: 1.25rem;
  font-weight: 500;
  color: #94a3b8;
}

.value {
  font-size: 1.875rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -1px;
}

.metric-footer {
  margin-top: 16px;
  display: flex;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}

.status-msg {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
}

.border-top-accent {
  border-top: 5px solid #00A650 !important;
}

.bg-emerald-subtle {
  background-color: #F0FDF4 !important;
}

.border-emerald {
  border: 1px solid #DCFCE7 !important;
}

.bg-amber-subtle {
  background-color: #FEFCE8 !important;
}

.border-amber {
  border: 1px solid #FEF08A !important;
}

.bg-rose-subtle {
  background-color: #FEF2F2 !important;
}

.border-rose {
  border: 1px solid #FECACA !important;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.dot-emerald { background-color: #10B981; }
.dot-amber { background-color: #F59E0B; }
.dot-rose { background-color: #F43F5E; }

.lh-16 {
  line-height: 1.6 !important;
}
</style>
